import React, { useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useAppTakeoverPdp } from 'features/app-takeover/hooks/use-app-takeover';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import {
  displayValueStyleProps,
  incrementorStyleProps,
} from 'features/menu/components/product-detail/product-cta/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { useAddToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useProductTotalPrice } from 'features/menu/components/product-wizard/use-product-total-price';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { NumberInput } from 'components/ucl';
import { EntryTypeEnum } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { logButtonClick, logRBIEvent } from 'state/amplitude';
import { ClickEventComponentNames, CustomEventNames, EventTypes } from 'state/amplitude/constants';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { MAX_CART_QUANTITY, MIN_CART_QUANTITY } from 'utils/cart';
import { HapticsImpactStyle } from 'utils/haptic/types';
import { routes } from 'utils/routing';

export const AddToCartCta = () => {
  const router = useRouter();
  const { cartEntries } = useCart();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const {
    selectedProduct,
    slotsSelections,
    productQuantity,
    setProductQuantity,
  } = useProductWizardContext();
  const {
    cartIdEditing,
    closeWizard,
    rewardBenefitId,
    offerId,
    id,
    rewardId,
  } = useProductWizardFlow();
  const { isTakeoverActive } = useAppTakeoverPdp(id);
  const addToCart = useAddToCart();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  const limitPerOrder =
    selectedProduct.type === EntryTypeEnum.COMBO || selectedProduct.type === EntryTypeEnum.ITEM
      ? selectedProduct.quantity?.max ?? MAX_CART_QUANTITY
      : MAX_CART_QUANTITY;

  const quantityOnHydratedCart = cartEntries.reduce(
    (sum, cartEntry) =>
      cartEntry._id === selectedProduct.id && cartEntry.cartId !== cartIdEditing
        ? sum + cartEntry.quantity
        : sum,
    0
  );

  const quantityOnMinimalCart = minimalCartEntries.reduce(
    (sum, cartEntry) =>
      cartEntry.id === selectedProduct.id && cartEntry.cartId !== cartIdEditing
        ? sum + cartEntry.quantity
        : sum,
    0
  );

  const availableCartQuantity = limitPerOrder - quantityOnHydratedCart - quantityOnMinimalCart;
  const maxCartQuantityMet = productQuantity > availableCartQuantity;

  const { formattedTotalPrice } = useProductTotalPrice({
    id,
    selectedProduct,
    slotsSelections,
    productQuantity,
    cartIdEditing,
    rewardId,
  });

  const buttonLabel = useMemo(() => {
    if (maxCartQuantityMet) {
      return t('limitReached');
    }

    const addToOrderTextKey = cartIdEditing ? 'update' : 'addToCart';
    const addToOrderText = t(addToOrderTextKey);
    return `${addToOrderText} - ${formattedTotalPrice}`;
  }, [cartIdEditing, formattedTotalPrice, maxCartQuantityMet]);

  const handleAddToCart = () => {
    addToCart(selectedProduct, productQuantity, slotsSelections);
    if (cartIdEditing) {
      closeWizard();
    } else {
      // dismissTo pops ALL stacked modals and reuses the existing menu tab — avoiding the (tabs)
      // rebuild/leak of dismissAll()+replace and the orphaned modal left by a plain navigate().
      // Works because `/menu` is the root stack's `(tabs)` anchor, a sibling of the `(modals)`
      // group, so dismissTo pops the whole modal subtree off the root. Revisit if the topology
      // changes (menu no longer the root anchor, or product modal stacked with menu).
      router.dismissTo(routes.menu);
    }
  };

  const handleQuantityUpdate = (value: number) => {
    setProductQuantity((prevValue: number) => {
      const action = prevValue < value ? 'increase' : 'decrease';
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: 'Product Incrementor',
        },
      });
      logRBIEvent(
        {
          name: CustomEventNames.ITEM_QUANTITY_MODIFIED,
          type: EventTypes.Other,
          attributes: {
            increaseOrDecrease: action,
            totalQuantity: value,
          },
        },
        { skipLoggingToBraze: true }
      );
      return value;
    });
  };

  const isDisabled = maxCartQuantityMet;
  // FIXME: disallow changing quantity for rewards incentives - CartV2 and Hydration can't handle it yet
  const isEditingCartEntryWithReward = appliedLoyaltyRewards[cartIdEditing ?? ''];

  const isOneMenuComboReward = !!rewardBenefitId && selectedProduct.type === EntryTypeEnum.COMBO;

  const showIncrementor =
    !maxCartQuantityMet && !isEditingCartEntryWithReward && !offerId && !isOneMenuComboReward;

  const disableIncrementQuantity = maxCartQuantityMet;
  const disableIncrementor = maxCartQuantityMet;

  const productName = selectedProduct.name?.locale ?? '';
  const ariaLabels = useMemo(() => {
    const ariaLabelKey = cartIdEditing
      ? 'updateQuantityAndItemWithPrice'
      : 'addQuantityAndItemWithPrice';

    return {
      ariaLabel: t(ariaLabelKey, {
        itemName: productName,
        quantity: productQuantity,
        price: formattedTotalPrice,
      }),
      decrementAriaLabel: t('decrementAriaLabel', { productName }),
      incrementAriaLabel: t('incrementAriaLabel', { productName }),
      quantityAriaLabel: t('quantityAriaLabel', { productQuantity, productName }),
    };
  }, [cartIdEditing, formattedTotalPrice, productName, productQuantity]);

  return (
    <>
      {showIncrementor && (
        <NumberInput
          displayedValue={productQuantity}
          value={productQuantity}
          onChange={handleQuantityUpdate}
          min={MIN_CART_QUANTITY}
          max={availableCartQuantity}
          hideNumberInput
          disableIncrement={disableIncrementQuantity}
          disabled={disableIncrementor}
          ariaLabel={ariaLabels.quantityAriaLabel}
          decrementAriaLabel={ariaLabels.decrementAriaLabel}
          incrementAriaLabel={ariaLabels.incrementAriaLabel}
          displayValueStyleProps={displayValueStyleProps}
          incrementorStyleProps={incrementorStyleProps}
          hapticFeedbackStyle={HapticsImpactStyle.Light}
        />
      )}
      <ActionButton
        accessibilityLabel={ariaLabels.ariaLabel}
        testID="product-home-action-button"
        disabled={isDisabled}
        onPress={handleAddToCart}
        flex={1}
        marginLeft={showIncrementor ? '$6' : '0'}
        variant={
          isTakeoverActive ? ActionButtonVariants.PRIMARY_TAKEOVER : ActionButtonVariants.PRIMARY
        }
      >
        {buttonLabel}
      </ActionButton>
    </>
  );
};
